import { create } from 'zustand';
import { useBackendStore } from '@/store/backendStore';
import type { AppMember, AppInvite, AppRole, InviteInput } from '@/backend/types';

interface MembersState {
  members: AppMember[];
  isLoading: boolean;
  error: string | null;
  lastInvite: AppInvite | null;
  load: (appId: string) => Promise<void>;
  invite: (appId: string, input: InviteInput) => Promise<AppInvite>;
  accept: (token: string) => Promise<{ appId: string; role: AppRole }>;
  setRole: (appId: string, userId: string, role: AppRole) => Promise<void>;
  remove: (appId: string, userId: string) => Promise<void>;
  reset: () => void;
}

const platform = () => useBackendStore.getState().platform;

export const useMembersStore = create<MembersState>((set, get) => ({
  members: [],
  isLoading: false,
  error: null,
  lastInvite: null,

  load: async (appId) => {
    set({ isLoading: true, error: null });
    try {
      set({ members: await platform().listMembers(appId), isLoading: false });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load members', isLoading: false });
    }
  },

  invite: async (appId, input) => {
    const invite = await platform().inviteToApp(appId, input);
    set({ lastInvite: invite });
    return invite;
  },

  accept: (token) => platform().acceptInvite(token),

  setRole: async (appId, userId, role) => {
    await platform().setMemberRole(appId, userId, role);
    await get().load(appId);
  },

  remove: async (appId, userId) => {
    await platform().removeMember(appId, userId);
    set({ members: get().members.filter((m) => m.userId !== userId) });
  },

  reset: () => set({ members: [], error: null, lastInvite: null, isLoading: false }),
}));
